import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Search, Download, Eye } from 'lucide-react';
import { disputesApi, API_BASE } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const DisputeStatus = ({ status }) => {
  const map = {
    awaiting_response: { label: 'Awaiting', cls: 'text-[#4b5320] border-[#4b5320]/40 bg-[#4b5320]/10' },
    resolved: { label: 'Resolved', cls: 'text-[#7fb685] border-[#7fb685]/40 bg-[#7fb685]/10' },
    rejected: { label: 'Rejected', cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
  };
  const s = map[status] || map.awaiting_response;
  return <div className={`inline-flex items-center text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm border ${s.cls}`}>{s.label}</div>;
};

const Disputes = () => {
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [disputes, setDisputes] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    disputesApi.list().then(r => setDisputes(r.data)).catch(() => {});
  }, []);

  const filtered = disputes.filter(d => {
    const matchQ = (d.clientName || '').toLowerCase().includes(query.toLowerCase()) || d.bureau.toLowerCase().includes(query.toLowerCase());
    const matchF = filter === 'all' || d.status === filter;
    return matchQ && matchF;
  });

  const stats = {
    total: disputes.length,
    awaiting: disputes.filter(d => d.status === 'awaiting_response').length,
    resolved: disputes.filter(d => d.status === 'resolved').length,
  };

  const viewLetter = async (disputeId) => {
    try {
      const r = await disputesApi.get(disputeId);
      setActive(r.data);
      setOpen(true);
    } catch {
      toast({ title: 'Could not load letter' });
    }
  };

  const downloadPdf = (disputeId) => {
    const token = localStorage.getItem('infantry_token');
    fetch(`${API_BASE}/disputes/${disputeId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dispute_${disputeId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast({ title: 'PDF downloaded' });
      })
      .catch(() => toast({ title: 'Download failed' }));
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Operations</div>
          <h1 className="font-display text-4xl text-stone-900">Disputes</h1>
          <p className="text-sm text-stone-700 mt-2">All dispute letters across your roster.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: 'Total Disputes', value: stats.total },
          { label: 'Awaiting Response', value: stats.awaiting },
          { label: 'Resolved', value: stats.resolved },
        ].map((s, i) => (
          <div key={i} className="border border-stone-200/80 bg-[#ffffff] p-6 rounded-sm relative">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#4b5320] to-transparent"></div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">{s.label}</div>
            <div className="font-display text-3xl text-stone-900">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search disputes..." className="pl-10 bg-[#fafaf6] border-stone-200 rounded-sm h-10" />
        </div>
        <div className="flex items-center gap-1 border border-stone-200 bg-[#fafaf6] rounded-sm px-1">
          {['all', 'awaiting_response', 'resolved'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
                filter === f ? 'bg-[#4b5320] text-white font-semibold' : 'text-stone-700 hover:text-stone-900'
              }`}
            >
              {f.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-stone-200/80 text-xs uppercase tracking-widest text-stone-500">
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Bureau</div>
          <div className="col-span-1">Round</div>
          <div className="col-span-3">Type</div>
          <div className="col-span-2">Sent</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        <div className="divide-y divide-stone-200/80">
          {filtered.map(d => (
            <div key={d.id} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-stone-100/40 transition-colors items-center">
              <div className="col-span-3">
                <Link to={`/app/clients/${d.clientId}`} className="text-stone-900 text-sm font-medium hover:text-[#4b5320]">{d.clientName}</Link>
                <div className="text-xs text-stone-500">{d.items?.length || 0} item(s)</div>
              </div>
              <div className="col-span-2 text-sm text-stone-700">{d.bureau}</div>
              <div className="col-span-1 font-mono text-sm text-stone-900">#{d.round}</div>
              <div className="col-span-3 text-sm text-stone-700">{d.type}</div>
              <div className="col-span-2 font-mono text-xs text-stone-700">{d.dateSent}</div>
              <div className="col-span-1 flex justify-end items-center gap-2">
                <DisputeStatus status={d.status} />
                <button onClick={() => viewLetter(d.id)} className="text-stone-700 hover:text-[#4b5320]" title="View letter">
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-6 py-16 text-center text-stone-500">
              {disputes.length === 0 ? 'No disputes yet. Generate one from a client file.' : 'No disputes match your filters.'}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#ffffff] border-stone-200 text-stone-800 rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Dispute Letter</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="text-xs text-stone-700 mb-2">
              {active.clientName} · {active.bureau} · Round {active.round} · {active.type}
            </div>
          )}
          <div className="bg-[#f7f6f0] border border-stone-200 rounded-sm p-6 max-h-[480px] overflow-auto">
            <pre className="font-mono text-xs text-stone-700 whitespace-pre-wrap leading-relaxed">{active?.letter || 'No letter content.'}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-stone-300 bg-transparent text-stone-700 hover:bg-stone-100 rounded-sm">Close</Button>
            <Button onClick={() => downloadPdf(active.id)} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Disputes;
